import { Container } from '@/components/layout/Container';
import { GlobalsAwardsFragment, GlobalsModuleDefaultsFragment, Maybe, ModuleBuilderFragment } from '@/graphql';
import { notEmpty } from '@/lib/typescript-helpers';
import AccordionListModule from '@/modules/AccordionList';
import BreatherHeadingModule from '@/modules/BreatherHeading';
import CapabilitiesLinkGridModule, { CapabilitiesLinkGridModuleProps } from '@/modules/capabilities/LinkGrid';
import CompetitorComparisonTableModule from '@/modules/CompetitorComparisonTable';
import CTAModule from '@/modules/CTA';
import CTABannerModule from '@/modules/CTABanner';
import FeatureGroupsModule from '@/modules/FeatureGroups';
import FeatureTilesModule from '@/modules/FeatureTiles';
import FormFooterModule from '@/modules/FormFooter';
import GlowQuoteModule from '@/modules/GlowQuote';
import GradientFooterModule from '@/modules/GradientFooter';
import HowFrontWorksVideoBannerModule from '@/modules/HowFrontWorksVideoBanner';
import IconCardsModule from '@/modules/IconCards';
import IllustrationHeroModule from '@/modules/IllustrationHero';
import IntegrationsCloudModule from '@/modules/IntegrationsCloud';
import LargeAssetAndGridModule from '@/modules/LargeAssetAndGrid';
import LogoFarmModule, { LogoFarmModuleProps } from '@/modules/LogoFarm';
import MediaAccordionModule from '@/modules/MediaAccordion';
import ProductOverviewModule from '@/modules/ProductOverview';
import ResourceCards from '@/modules/ResourceCards';
import SimpleCTAFooterModule, { SimpleCTAFooterModuleProps } from '@/modules/SimpleCTAFooter';
import SimpleHeroModule, { SimpleHeroModuleProps } from '@/modules/SimpleHero';
import SocialProofCardCarouselModule from '@/modules/SocialProofCardCarousel';
import StatisticsBlockModule from '@/modules/StatisticsBlock';
import TextColumnsModule from '@/modules/TextColumns';
import TextIllustrationColumnsModule from '@/modules/TextIllustrationColumns';
import TextListTableModule from '@/modules/TextListTable';
import UIHeroModule from '@/modules/UIHero';
import VideoBannerModule from '@/modules/VideoBanner';
import VideoSectionModule, { VideoSectionModuleProps } from '@/modules/VideoSection';
import { FC } from 'react';

export interface ModuleBuilderProps extends PropsWithClassName {
  props: CraftModuleFragment<ModuleBuilderFragment>;
  config?: {
    capabilitiesLinkGrid?: {
      links?: CapabilitiesLinkGridModuleProps['links'];
    };
    formFooter?: {
      children?: FC<{ confirmationText: string | null | undefined }>;
    };
    logoFarm?: {
      awards?: LogoFarmModuleProps['awards'];
    };
    simpleCtaFooter?: {
      ctasOverride?: SimpleCTAFooterModuleProps['ctaOverride'];
    };
    simpleHero?: {
      assetOverride?: SimpleHeroModuleProps['assetOverride'];
      ctasOverride?: SimpleHeroModuleProps['ctaOverride'];
    };
    videoSection?: {
      ctasOverride?: VideoSectionModuleProps['ctaOverride'];
    };
  };
}

const ModuleBuilder: FC<ModuleBuilderProps> = ({ props, className, config }) => {
  if (!Array.isArray(props)) return null;

  return (
    <main className={className}>
      {props.filter(notEmpty).map((block) => {
        if ('module' in block) {
          const moduleProps = Array.isArray(block.module) ? block.module[0] : block.module;

          if (!moduleProps) return null;

          switch (moduleProps.__typename) {
            case 'moduleAccordionlist_BlockType':
              return (
                <Container key={block.id}>
                  <AccordionListModule props={moduleProps} />
                </Container>
              );

            case 'moduleBreatherHeading_BlockType':
              return <BreatherHeadingModule key={block.id} props={moduleProps} />;

            case 'moduleCompetitorcomparisontable_BlockType':
              return (
                <Container key={block.id}>
                  <CompetitorComparisonTableModule props={moduleProps} />
                </Container>
              );

            case 'moduleCta_BlockType':
              return (
                <Container key={block.id}>
                  <CTAModule props={moduleProps} />
                </Container>
              );

            case 'moduleCtaBanner_BlockType':
              return (
                <Container key={block.id}>
                  <CTABannerModule props={moduleProps} />
                </Container>
              );

            case 'moduleFeatureGroups_BlockType':
              return <FeatureGroupsModule key={block.id} props={moduleProps} />;

            case 'moduleFeatureTiles_BlockType':
              return (
                <Container key={block.id}>
                  <FeatureTilesModule props={moduleProps} />
                </Container>
              );

            case 'moduleFormfooter_BlockType':
              return (
                <FormFooterModule key={block.id} props={moduleProps}>
                  {config?.formFooter?.children ? (
                    <config.formFooter.children confirmationText={moduleProps.confirmationText} />
                  ) : null}
                </FormFooterModule>
              );

            case 'moduleGlowquote_BlockType':
              return <GlowQuoteModule key={block.id} props={moduleProps} />;

            case 'moduleGradientFooter_BlockType':
              return <GradientFooterModule key={block.id} props={moduleProps} />;

            case 'moduleHowfrontworksvideobanner_BlockType':
              return <HowFrontWorksVideoBannerModule key={block.id} props={moduleProps} />;

            case 'moduleIconCards_BlockType':
              return (
                <Container key={block.id}>
                  <IconCardsModule props={moduleProps} />
                </Container>
              );

            case 'moduleIllustrationhero_BlockType':
              return <IllustrationHeroModule key={block.id} props={moduleProps} />;

            case 'moduleIntegrationscloud_BlockType':
              return (
                <Container key={block.id}>
                  <IntegrationsCloudModule props={moduleProps} />
                </Container>
              );

            case 'moduleLargeAssetAndGrid_BlockType':
              return (
                <Container key={block.id}>
                  <LargeAssetAndGridModule props={moduleProps} />
                </Container>
              );

            case 'moduleLogofarm_BlockType':
              return (
                <Container key={block.id}>
                  <LogoFarmModule props={moduleProps} awards={config?.logoFarm?.awards} />
                </Container>
              );

            case 'moduleMediaAccordion_BlockType':
              return (
                <Container key={block.id}>
                  <MediaAccordionModule props={moduleProps} />
                </Container>
              );

            case 'moduleProductoverview_BlockType':
              return (
                <Container key={block.id}>
                  <ProductOverviewModule props={moduleProps} />
                </Container>
              );

            case 'moduleResourceCards_BlockType':
              return (
                <Container key={block.id}>
                  <ResourceCards props={moduleProps} />
                </Container>
              );

            case 'moduleSimplectafooter_BlockType':
              return (
                <SimpleCTAFooterModule
                  key={block.id}
                  props={moduleProps}
                  ctaOverride={config?.simpleCtaFooter?.ctasOverride}
                />
              );

            case 'moduleSimplehero_BlockType':
              return (
                <SimpleHeroModule
                  key={block.id}
                  props={moduleProps}
                  assetOverride={config?.simpleHero?.assetOverride}
                  ctaOverride={config?.simpleHero?.ctasOverride}
                />
              );

            case 'moduleSocialProofCardCarousel_BlockType':
              return <SocialProofCardCarouselModule key={block.id} props={moduleProps} />;

            case 'moduleStatisticsblock_BlockType':
              return (
                <Container key={block.id}>
                  <StatisticsBlockModule props={moduleProps} />
                </Container>
              );

            case 'moduleTextColumns_BlockType':
              return (
                <Container key={block.id}>
                  <TextColumnsModule props={moduleProps} />
                </Container>
              );

            case 'moduleTextillustrationcolumns_BlockType':
              return (
                <Container key={block.id}>
                  <TextIllustrationColumnsModule props={moduleProps} />
                </Container>
              );

            case 'moduleTextListTable_BlockType':
              return <TextListTableModule key={block.id} props={moduleProps} />;

            case 'moduleUihero_BlockType':
              return <UIHeroModule key={block.id} props={moduleProps} />;

            case 'moduleVideoBanner_BlockType':
              return <VideoBannerModule props={moduleProps} />;

            case 'moduleVideoSection_BlockType':
              return (
                <Container key={block.id}>
                  <VideoSectionModule props={moduleProps} ctaOverride={config?.videoSection?.ctasOverride} />
                </Container>
              );

            default:
              return null;
          }
        } else {
          switch (block.__typename) {
            case 'moduleBuilder_moduleCapabilitiesLinkGrid_BlockType':
              return (
                <CapabilitiesLinkGridModule key={block.id} links={config?.capabilitiesLinkGrid?.links ?? undefined} />
              );

            default:
              return null;
          }
        }
      })}
    </main>
  );
};

export default ModuleBuilder;

export interface ParseModuleBuilderPropsInput {
  entry?: Maybe<{ moduleBuilder?: Maybe<Maybe<ModuleBuilderFragment>[]> }>;
  moduleDefaults?: Maybe<GlobalsModuleDefaultsFragment>;
  awards?: Maybe<GlobalsAwardsFragment>;
}

export type ModularPageProps = {
  modularPageProps: Omit<ParseModuleBuilderPropsInput, 'moduleDefaults'>;
};

export const parseModuleBuilderProps = ({
  entry,
  moduleDefaults,
  awards,
  ...props
}: ParseModuleBuilderPropsInput): ModularPageProps => {
  if (!entry || !entry.moduleBuilder) {
    throw new Error('Module builder props are missing');
  }

  if (!moduleDefaults) {
    throw new Error('Module defaults are missing');
  }

  let hasModuleWithAwards = false;

  entry.moduleBuilder
    .filter(notEmpty)
    .map((block) => {
      if ('useDefaultModuleOptions' in block && block.useDefaultModuleOptions === true) {
        // Replace the module props with the defaults
        block.module = moduleDefaults[block.__typename.slice(14, -10)];
      }

      if (!('module' in block) || !block.module[0]) return null;

      const blockModule = block.module[0];

      if (blockModule.__typename === 'moduleLogofarm_BlockType') {
        hasModuleWithAwards = hasModuleWithAwards || Boolean(blockModule.showAwards);
      }

      return block;
    })
    .filter(notEmpty);

  if (hasModuleWithAwards && !awards) {
    throw new Error('Award props are missing');
  }

  return {
    modularPageProps: {
      entry: {
        ...entry,
        moduleBuilder: entry.moduleBuilder,
      },
      // Strip out awards data if no module that needs awards is added to the page
      awards: hasModuleWithAwards ? awards : null,
    },
    ...props,
  };
};
